import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Cookie, X, CheckCircle } from "@phosphor-icons/react";

const STORAGE_KEY = "bb.consent";

function loadConsent() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function saveConsent(value) {
  try {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ ...value, ts: new Date().toISOString(), v: 1 })
    );
  } catch {
    /* storage disabled — nothing to persist */
  }
}

/**
 * DSGVO-konformer Transparenz-Hinweis. Da wir ausschließlich technisch
 * notwendige Cookies setzen (§ 165 Abs. 3 TKG 2021), genügt eine klare
 * Information — keine Pflicht zum Consent-Toggle. Nutzer:innen können den
 * Hinweis trotzdem jederzeit erneut aufrufen.
 */
export default function CookieConsent() {
  const [state, setState] = useState(() => loadConsent());
  const [showDetails, setShowDetails] = useState(false);

  useEffect(() => {
    const onReopen = () => setState(null);
    window.addEventListener("bb:open-cookie-consent", onReopen);
    return () => window.removeEventListener("bb:open-cookie-consent", onReopen);
  }, []);

  if (state?.acknowledged) return null;

  const accept = () => {
    const v = { acknowledged: true, essential: true, optional: false };
    saveConsent(v);
    setState(v);
  };

  return (
    <div
      className="fixed inset-x-0 bottom-0 z-[60] p-3 sm:p-4 pointer-events-none"
      data-testid="cookie-consent-root"
    >
      <div
        className="max-w-3xl mx-auto pointer-events-auto eid-card shadow-xl border border-white/10 bg-[#121212] overflow-hidden"
        role="dialog"
        aria-labelledby="cookie-title"
        aria-describedby="cookie-desc"
      >
        <div className="p-4 sm:p-5 flex items-start gap-3">
          <span className="shrink-0 p-2 rounded-sm bg-[#00FA9A]/10 text-[#00FA9A]">
            <Cookie size={22} weight="regular" />
          </span>
          <div className="flex-1 min-w-0">
            <div className="flex items-baseline justify-between gap-2">
              <h3 id="cookie-title" className="text-base font-medium tracking-tight">
                Cookies &amp; Datenschutz
              </h3>
              <span className="text-[10px] font-mono uppercase tracking-wider text-[#00FA9A] bg-[#00FA9A]/10 px-2 py-0.5 rounded-full">
                Privacy by Design
              </span>
            </div>
            <p id="cookie-desc" className="text-sm text-zinc-400 mt-1.5 leading-relaxed">
              Bodenblick verwendet <strong>ausschließlich technisch notwendige Cookies</strong>
              {" "}(Login-Session + deine Entscheidung hier). Wir setzen keine Tracking-,
              Analyse- oder Werbe-Cookies und geben keine Daten an Dritte zur
              Profilbildung weiter.{" "}
              <Link to="/datenschutz" className="underline text-white">
                Datenschutzerklärung
              </Link>
              .
            </p>

            {showDetails && (
              <div
                className="mt-3 border border-white/10 rounded-sm p-3 text-xs space-y-2"
                data-testid="cookie-details"
              >
                <Row
                  name="session_token"
                  purpose="Authentifizierung (HttpOnly, Secure)"
                  duration="7 Tage"
                />
                <Row
                  name="bb.consent"
                  purpose="Speichert deine Entscheidung hier (LocalStorage)"
                  duration="12 Monate"
                />
                <p className="text-[11px] text-zinc-500 pt-1">
                  Keine Marketing-, Analyse- oder Third-Party-Cookies aktiv. Alle
                  externen Datenquellen werden ausschließlich serverseitig
                  abgefragt.
                </p>
              </div>
            )}

            <div className="mt-3 flex flex-wrap gap-2 items-center">
              <button
                onClick={accept}
                className="inline-flex items-center gap-1.5 bg-[#00FA9A] text-black hover:bg-[#33FBAE] transition-colors rounded-full px-4 py-2 text-sm font-medium"
                data-testid="cookie-accept"
              >
                <CheckCircle size={16} weight="fill" /> OK, verstanden
              </button>
              <button
                onClick={() => setShowDetails((v) => !v)}
                className="text-sm text-zinc-400 hover:text-white underline"
                data-testid="cookie-toggle-details"
              >
                {showDetails ? "Details verbergen" : "Details anzeigen"}
              </button>
              <Link
                to="/datenschutz"
                className="text-sm text-zinc-400 hover:text-white underline"
                data-testid="cookie-datenschutz-link"
              >
                Datenschutz
              </Link>
              <Link
                to="/impressum"
                className="text-sm text-zinc-400 hover:text-white underline"
                data-testid="cookie-impressum-link"
              >
                Impressum
              </Link>
            </div>
          </div>
          <button
            onClick={accept}
            className="shrink-0 text-zinc-500 hover:text-white"
            aria-label="Schließen"
            data-testid="cookie-close"
          >
            <X size={16} />
          </button>
        </div>
      </div>
    </div>
  );
}

function Row({ name, purpose, duration }) {
  return (
    <div className="flex items-baseline justify-between gap-2 border-b border-white/10 pb-1 last:border-0">
      <span className="font-mono">{name}</span>
      <span className="flex-1 px-2 text-zinc-400">{purpose}</span>
      <span className="font-mono text-zinc-500">{duration}</span>
    </div>
  );
}

/**
 * Footer-Link zum erneuten Öffnen des Banners. Triggert ein Custom-Event.
 */
export function ReopenCookieLink({ className = "hover:text-white" }) {
  const onClick = (e) => {
    e.preventDefault();
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch {}
    window.dispatchEvent(new Event("bb:open-cookie-consent"));
  };
  return (
    <a
      href="#cookies"
      onClick={onClick}
      className={className}
      data-testid="reopen-cookie-consent"
    >
      Cookie-Einstellungen
    </a>
  );
}
